import * as Dropdown from '@radix-ui/react-dropdown-menu'
import { MoreVertical, type LucideIcon } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'

import { cn } from './cn'
import { useDisabled } from './control-scope'
import {
  FLOATING_SURFACE_CLASS,
  FOCUS_RING,
  MENU_ITEM_CLASS,
  MENU_SEPARATOR_CLASS,
} from './recipes'
import { usePortalThemeClass } from './theme-scope'

export type MenuAction = {
  key: string
  label: string
  icon?: LucideIcon
  /** Red ink and a separator above — a delete, a revoke. */
  danger?: boolean
  disabled?: boolean
  onSelect: () => void
}

/**
 * The row's action menu.
 *
 * Radix handles the roving focus, escape, outside-press and the portal, so the
 * menu of the last row in a table is not clipped by the scroll container it
 * sits in.
 */
export function Menu({
  actions,
  trigger,
  label,
  reads,
}: {
  actions: MenuAction[]
  /** Defaults to a kebab button. */
  trigger?: ReactNode
  /** Accessible name for the default trigger. */
  label: string
  /** Marks a menu whose entries only take the reader somewhere, so a
   *  read-only surface leaves it open-able. A menu of things to do is shut. */
  reads?: boolean
}) {
  const themeClass = usePortalThemeClass()
  const refused = useDisabled() && !reads

  if (actions.length === 0) return null

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild disabled={refused}>
        {trigger ?? (
          <button
            type="button"
            aria-label={label}
            disabled={refused}
            className={`rounded-md p-1 text-muted-foreground hover:bg-line-soft disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent ${FOCUS_RING}`}
          >
            <MoreVertical size={16} />
          </button>
        )}
      </Dropdown.Trigger>

      <Dropdown.Portal>
        <Dropdown.Content
          align="end"
          sideOffset={4}
          className={cn(themeClass, FLOATING_SURFACE_CLASS, 'min-w-44 p-1')}
        >
          {actions.map((action, index) => {
            const Icon = action.icon
            const startsDangerGroup =
              action.danger && !actions[index - 1]?.danger && index > 0

            return (
              <Fragment key={action.key}>
                {startsDangerGroup ? (
                  <Dropdown.Separator className={MENU_SEPARATOR_CLASS} />
                ) : null}
                <Dropdown.Item
                  disabled={action.disabled}
                  onSelect={action.onSelect}
                  className={cn(
                    MENU_ITEM_CLASS,
                    'text-sm',
                    // Radix drives the ground from the highlight, which
                    // follows the keyboard as well as the pointer.
                    'data-[highlighted]:bg-line-soft',
                    'outline-none select-none',
                    'data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed',
                    action.danger && 'text-destructive',
                  )}
                >
                  {Icon ? <Icon size={14} /> : null}
                  {action.label}
                </Dropdown.Item>
              </Fragment>
            )
          })}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  )
}
